import { QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { PlatformSlug } from 'constants/consts'

export type QuestionAnswerMode = 'single' | 'multiple' | 'json'
export type QuestionInputKind = 'html' | 'stem'
export type OptionInputKind = 'html' | 'plain'

export type QuestionTypeConfig = {
  questionInput: QuestionInputKind
  optionInput: OptionInputKind // plain for GRE only, html for all other question types
  answerMode: QuestionAnswerMode
  hasPoints?: boolean // points are only on NDEB multi and DLOSCE multi
  requiredCorrectCount?: number
  showMultipleSelectionLabel?: boolean // multiple selection label is only on NDEB multi
}

const SINGLE_HTML_OPTIONS: QuestionTypeConfig = {
  answerMode: 'single',
  hasPoints: false,
  showMultipleSelectionLabel: false,
  questionInput: 'html',
  optionInput: 'html',
}

const MULTI_HTML_OPTIONS: QuestionTypeConfig = {
  answerMode: 'multiple',
  hasPoints: false,
  showMultipleSelectionLabel: false,
  questionInput: 'html',
  optionInput: 'html',
}

const GRE_SINGLE: QuestionTypeConfig = {
  ...SINGLE_HTML_OPTIONS,
  optionInput: 'plain',
}

const GRE_MULTI: QuestionTypeConfig = {
  ...MULTI_HTML_OPTIONS,
  optionInput: 'plain',
}

export const QUESTION_TYPE_CONFIG: Record<QuestionTypeEnum, QuestionTypeConfig> = {
  [QuestionTypeEnum.SingleChoice]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.SingleChoiceInbde]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.CaseNBDHE]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.TestletNBDHE]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.SingleChoiceCARS]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.SingleChoiceNDEB]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.SingleChoiceDLOSCE]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.SingleChoiceNBEO]: SINGLE_HTML_OPTIONS,
  [QuestionTypeEnum.MultiChoiceNDEB]: {
    ...MULTI_HTML_OPTIONS,
    hasPoints: true,
    showMultipleSelectionLabel: true,
  },
  [QuestionTypeEnum.MultiChoiceDLOSCE]: {
    ...MULTI_HTML_OPTIONS,
    hasPoints: true,
  },
  [QuestionTypeEnum.MultiChoiceNBEO]: MULTI_HTML_OPTIONS,
  [QuestionTypeEnum.RxWritingNDEB]: {
    answerMode: 'json',
    hasPoints: false,
    showMultipleSelectionLabel: false,
    questionInput: 'stem',
    optionInput: 'html',
  },
  [QuestionTypeEnum.SingleChoiceGRE]: GRE_SINGLE,
  [QuestionTypeEnum.PassageSingleChoiceGRE]: GRE_SINGLE,
  [QuestionTypeEnum.MultiChoiceGre]: GRE_MULTI,
  [QuestionTypeEnum.PassageMultiChoiceGRE]: GRE_MULTI,
  [QuestionTypeEnum.SentenceEquivalence]: {
    ...GRE_MULTI,
    requiredCorrectCount: 2,
  },
  [QuestionTypeEnum.TextCompletion]: {
    answerMode: 'json',
    hasPoints: false,
    showMultipleSelectionLabel: false,
    questionInput: 'html',
    optionInput: 'plain',
  },
  [QuestionTypeEnum.NumericResponse]: {
    answerMode: 'json',
    hasPoints: false,
    showMultipleSelectionLabel: false,
    questionInput: 'html',
    optionInput: 'plain',
  },
}

export const getQuestionTypeConfig = (type?: QuestionTypeEnum | null): QuestionTypeConfig => {
  if (!type) {
    return QUESTION_TYPE_CONFIG[QuestionTypeEnum.SingleChoice]
  }

  return QUESTION_TYPE_CONFIG[type]
}

export const isPlainTextOptions = (type?: QuestionTypeEnum | null) =>
  getQuestionTypeConfig(type).optionInput === 'plain'

export const PASSAGE_QUESTION_TYPES = [
  QuestionTypeEnum.PassageSingleChoiceGRE,
  QuestionTypeEnum.PassageMultiChoiceGRE,
] as const

export const isPassageQuestion = (type?: QuestionTypeEnum) =>
  !!type && (PASSAGE_QUESTION_TYPES as readonly QuestionTypeEnum[]).includes(type)

export const isGREPassagesAvailable = (platformSlug?: string) =>
  !!platformSlug && [PlatformSlug.GREBooster].includes(platformSlug as PlatformSlug)
